'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Cookie, X } from 'lucide-react';

const CONSENT_KEY = 'dm_cookie_consent';

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      // Small delay so it doesn't flash on first render
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  function acceptAll() {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ essential: true, analytics: true, functional: true, timestamp: Date.now() }));
    setVisible(false);
  }

  function acceptEssential() {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ essential: true, analytics: false, functional: false, timestamp: Date.now() }));
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 sm:p-6">
      <div className="max-w-3xl mx-auto bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-2xl shadow-2xl shadow-black/60 p-5">
        <div className="flex items-start gap-4">
          <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0 mt-0.5">
            <Cookie className="w-4 h-4 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-white mb-1">We use cookies</h3>
            <p className="text-xs text-slate-400 leading-relaxed mb-4">
              We use essential cookies to keep you logged in and functional cookies to remember your preferences. With your consent, we also use analytics cookies to improve the platform.{' '}
              <Link href="/cookies" className="text-amber-400 hover:text-amber-300 underline underline-offset-2">
                Cookie Policy
              </Link>
              {' '}·{' '}
              <Link href="/privacy" className="text-amber-400 hover:text-amber-300 underline underline-offset-2">
                Privacy Policy
              </Link>
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={acceptAll}
                className="flex-1 sm:flex-none bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-4 py-2 rounded-lg text-xs transition-all duration-150 active:scale-95"
              >
                Accept All
              </button>
              <button
                onClick={acceptEssential}
                className="flex-1 sm:flex-none border border-[hsl(222,25%,18%)] hover:border-slate-500 text-slate-300 hover:text-white px-4 py-2 rounded-lg text-xs transition-all duration-150"
              >
                Essential Only
              </button>
              <Link
                href="/cookies"
                className="text-xs text-slate-500 hover:text-slate-300 px-2 py-2 transition-colors"
              >
                Manage
              </Link>
            </div>
          </div>
          <button
            onClick={acceptEssential}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-white hover:bg-white/5 transition-all shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
